// Lógica pura del formulario de contacto (guía 8.11 y 15): validación, honeypot, rate
// limit y armado del aviso por mail. Vive separada del route handler para poder
// probarla sin levantar Next ni tocar el disco. Sin imports con "@/" a propósito:
// vitest corre sin alias.
//
// El formato de lo que llega por la red y de lo que se guarda en data/leads.jsonl es el
// mismo del sitio anterior (name, email, company, service, message, website): así una
// página vieja en caché, un script propio o quien lea el archivo no se rompe.

export type MotiveOption = { id: string; label: string };

/** Motivos que ofrece el formulario nuevo, en el orden de la guía 8.11. */
export const motiveOptions: readonly MotiveOption[] = [
  { id: "negocio", label: "Negocio" },
  { id: "prensa", label: "Prensa" },
  { id: "inversion", label: "Inversión y alianzas" },
  { id: "otro", label: "Otro" },
];

// Valores del formulario anterior. Se siguen aceptando (guía 8.11) y se guardan tal
// cual; solo cambia la etiqueta con la que llegan al mail. "otro" ya está arriba.
const legacyMotives: readonly MotiveOption[] = [
  { id: "yvyra", label: "Yvyra" },
  { id: "tajy", label: "Tajy" },
  { id: "kaaguy", label: "Ka'aguy" },
  { id: "custom", label: "Modelo a medida" },
];

const allMotives = [...motiveOptions, ...legacyMotives];

export const acceptedMotiveIds: readonly string[] = allMotives.map((m) => m.id);

export const FALLBACK_MOTIVE = "otro";

export function motiveLabel(id: string): string {
  return allMotives.find((m) => m.id === id)?.label ?? "Consulta general";
}

/** Límites de largo: los mismos del sitio anterior. */
export const LIMITS = { name: 120, email: 200, company: 160, message: 4000, service: 40, honeypot: 200 } as const;

export const MIN_NAME = 2;
export const MIN_MESSAGE = 10;

/** Nombre del campo trampa. Es el mismo del formulario anterior. */
export const HONEYPOT_FIELD = "website";

export const messages = {
  rateLimited: "Demasiados envíos. Probá de nuevo en un minuto.",
  badRequest: "Solicitud inválida.",
  name: "Escribí tu nombre.",
  email: "Escribí un mail válido, por ejemplo nombre@empresa.com.",
  message: `Contanos un poco más: al menos ${MIN_MESSAGE} caracteres.`,
} as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function clean(value: unknown, max: number): string {
  return typeof value === "string" ? value.trim().slice(0, max) : "";
}

export type Lead = {
  name: string;
  email: string;
  company: string;
  service: string;
  message: string;
};

export type FieldErrors = Partial<Record<"name" | "email" | "message", string>>;

export type ContactResult =
  | { kind: "bad-request" }
  | { kind: "honeypot" }
  | { kind: "invalid"; errors: FieldErrors }
  | { kind: "ok"; lead: Lead };

/** Valida un cuerpo ya parseado. No lanza: cualquier forma rara es "bad-request". */
export function validateContact(body: unknown): ContactResult {
  if (typeof body !== "object" || body === null || Array.isArray(body)) return { kind: "bad-request" };
  const b = body as Record<string, unknown>;

  // Un humano nunca ve este campo. Si viene con algo, es un bot: se le responde como
  // si hubiera salido bien para no darle pistas, y no se guarda nada.
  if (clean(b[HONEYPOT_FIELD], LIMITS.honeypot)) return { kind: "honeypot" };

  const name = clean(b.name, LIMITS.name);
  const email = clean(b.email, LIMITS.email);
  const company = clean(b.company, LIMITS.company);
  const message = clean(b.message, LIMITS.message);
  let service = clean(b.service, LIMITS.service);
  if (!acceptedMotiveIds.includes(service)) service = FALLBACK_MOTIVE;

  const errors: FieldErrors = {};
  if (name.length < MIN_NAME) errors.name = messages.name;
  if (!EMAIL_RE.test(email)) errors.email = messages.email;
  if (message.length < MIN_MESSAGE) errors.message = messages.message;
  if (Object.keys(errors).length > 0) return { kind: "invalid", errors };

  return { kind: "ok", lead: { name, email, company, service, message } };
}

// ── Rate limit ──
// En memoria, como el sitio anterior: alcanza para una sola instancia de PM2. Cada
// intento cuenta, incluso los rechazados, así que quien insiste sigue frenado.

export const RATE_LIMIT_WINDOW_MS = 60_000;
export const RATE_LIMIT_MAX = 5;

export function createRateLimiter({
  windowMs = RATE_LIMIT_WINDOW_MS,
  max = RATE_LIMIT_MAX,
  now = () => Date.now(),
}: { windowMs?: number; max?: number; now?: () => number } = {}) {
  const buckets = new Map<string, number[]>();

  // El proceso vive semanas: de vez en cuando se barren las IP que ya no tienen
  // intentos recientes para que el mapa no crezca sin límite.
  const sweep = (t: number) => {
    for (const [key, hits] of buckets) {
      if (hits.every((h) => t - h >= windowMs)) buckets.delete(key);
    }
  };

  return {
    /** Registra un intento y devuelve true si esa IP pasó el límite. */
    hit(ip: string): boolean {
      const t = now();
      if (buckets.size > 1000) sweep(t);
      const hits = (buckets.get(ip) ?? []).filter((h) => t - h < windowMs);
      hits.push(t);
      buckets.set(ip, hits);
      return hits.length > max;
    },
  };
}

export function getClientIp(headers: Headers): string {
  const forwarded = headers.get("x-forwarded-for");
  if (forwarded) return forwarded.split(",")[0].trim();
  return headers.get("x-real-ip") ?? "unknown";
}

// ── Aviso por mail ──

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Arma asunto y cuerpo del aviso. El motivo va en el asunto (guía 8.11). */
export function buildNotification(lead: Lead): { subject: string; html: string } {
  const label = motiveLabel(lead.service);
  // Un salto de línea en el nombre no tiene que poder partir el encabezado del mail.
  const oneLine = (s: string) => s.replace(/[\r\n]+/g, " ");
  const subject = `Contacto YvyrAI · ${oneLine(label)} · ${oneLine(lead.name)}`;
  const html = `
    <h2>Nuevo mensaje desde yvyrai.lat</h2>
    <p><strong>Motivo:</strong> ${escapeHtml(label)}</p>
    <p><strong>Nombre:</strong> ${escapeHtml(lead.name)}</p>
    <p><strong>Mail:</strong> ${escapeHtml(lead.email)}</p>
    <p><strong>Empresa u organización:</strong> ${escapeHtml(lead.company) || "Sin dato"}</p>
    <p><strong>Mensaje:</strong></p>
    <p style="white-space:pre-wrap">${escapeHtml(lead.message)}</p>
  `;
  return { subject, html };
}
